<template>
    <div class="ChatModal">
        <slot v-bind="{ open, close, loading, unread }" />

        <Modal
            v-if="currentOpenState"
            :gestures="false"
            class="ChatModal__modal"
            @close="close"
            @mounted="handleScroll"
        >
            <ChatThread
                ref="ChatThread"
                class="ChatModal__thread"
                :thread="thread"
                :messages="currentMessages"
                @sent="pushNewMessage"
            />
        </Modal>
    </div>
</template>

<style lang="less" type="text/less">
.ChatModal__modal {
    padding: 0;
}
.ChatModal__thread {
    height: 80vh;
}
@screen md {
    .ChatModal__thread {
        height: 90vh;
    }
}
</style>

<script type="text/babel">
import { ReplaySubject, combineLatest } from 'rxjs'
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'
import { take } from 'rxjs/operators'
import ChatProvider from 'dashboard-js/providers/ChatProvider'
import ChatThread from 'dashboard-js/components/chat/ChatThread'
import CreatesPopovers from 'dashboard-js/mixins/CreatesPopovers'
import HasSubscriptions from 'dashboard-js/mixins/HasSubscriptions'
import Modal from 'dashboard-js/components/Modal'
import RestoreOnPageBack from 'dashboard-js/mixins/RestoreOnPageBack'

export default {
    components: { Modal, ChatThread },

    mixins: [CreatesPopovers, HasSubscriptions, RestoreOnPageBack],

    props: {
        threadId: {},
        refresh: {},
        openInstantly: {
            default: false,
        },
    },

    data() {
        return {
            thread: null,
            currentMessages: null,
            currentOpenState: null,
            messages: new ReplaySubject(1),
            openState: new ReplaySubject(1),
        }
    },

    computed: {
        loading() {
            return this.currentMessages === null
        },
        me() {
            return this.$get(this.thread, 'participants').find(
                participant => participant.is_me === true
            )
        },
        unread() {
            const messages = this.loading ? [] : this.currentMessages

            const sentByMe = message =>
                message.user_type === this.me.user_type && message.user_id === this.me.user_id

            const readByMe = message =>
                this.me.last_read !== null &&
                moment(this.me.last_read).isAfter(moment(message.created_at))

            return messages.filter(message => {
                return !sentByMe(message) && !readByMe(message)
            })
        },
    },

    watch: {
        unread(unread) {
            if (unread.length > 0 && !this.currentOpenState) {
                this.showPopover()
            }
        },
    },

    created() {
        this.subscribe(this.messages, messages => (this.currentMessages = messages))
        this.subscribe(this.openState, openState => (this.currentOpenState = openState))
        this.subscribe(Echo.join(`chat_threads.${this.threadId}`)).listen(
            'ChatMessageSent',
            event => this.pushNewMessage(event.message)
        )

        this.subscribe(combineLatest([this.messages, this.openState]), ([, isOpen]) => {
            if (isOpen && this.unread.length > 0 && !Spark.impersonating) {
                this.markAsRead()
            }
        })

        // Allow parent to provide a refresh-stream that will trigger reload
        if (this.refresh != null) {
            this.subscribe(this.refresh, this.load)
        }

        this.load()
    },

    mounted() {
        if (this.openInstantly) {
            this.open()
        }
    },

    beforeDestroy() {
        this.saveState()
    },

    methods: {
        close() {
            this.openState.next(false)
            this.$emit('closed')
        },

        getSaveStateConfig() {
            return {
                cacheKey: this.getCacheKey(),
                ignoreProperties: ['messages', 'openState', 'subscriptions'],
            }
        },

        handleScroll() {
            clearAllBodyScrollLocks()
            disableBodyScroll(this.$refs.ChatThread.chatHistory, { reserveScrollBarGap: true })
        },

        load() {
            ChatProvider.getThread(this.threadId)
                .then(thread => (this.thread = thread))
                .then(thread => this.messages.next(thread.messages))
        },

        markAsRead() {
            ChatProvider.markAsRead(this.thread).then(participant =>
                Object.assign(this.me, participant.data.data)
            )
        },

        showPopover() {
            const message =
                this.unread.length === 1
                    ? `${this.unread.length} ny besked`
                    : `${this.unread.length} nye beskeder`

            this.popover(this.$el, message, { placement: 'left' })
        },

        pushNewMessage(message) {
            this.messages
                .pipe(take(1))
                .subscribe(items => this.messages.next(items.concat([message])))
        },

        open() {
            this.openState.next(true)
        },
    },
}
</script>
